import { useCallback, useEffect, useState } from "react";
import { Badge, Card } from "@/components/ui";
import { healthService, type HealthSnapshot, type VersionInfo } from "@/services/health.service";

export default function StatusPage() {
  const [health, setHealth] = useState<HealthSnapshot | null>(null);
  const [version, setVersion] = useState<VersionInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [h, v] = await Promise.all([healthService.snapshot(), healthService.version()]);
      setHealth(h);
      setVersion(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Falha ao consultar a API");
      setHealth(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const tone = (up: boolean | undefined) => (loading ? "neutral" : up ? "ok" : "danger");
  const label = (up: boolean | undefined) => (loading ? "..." : up ? "Online" : "Offline");

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-semibold">Okton Builder AI</h1>
        <p className="mt-2 max-w-2xl text-sm text-ink-muted">
          Infraestrutura executável instalada. Esta página consulta{" "}
          <code className="font-mono text-ink">/api/health</code> e confirma que frontend,
          backend e PostgreSQL estão conversando.
        </p>
      </div>

      <Card
        title="Status"
        action={
          <button
            onClick={() => void load()}
            className="rounded-md border border-line px-3 py-1 font-mono text-xs text-ink-muted transition-colors hover:border-brand hover:text-brand"
          >
            {loading ? "verificando..." : "reverificar"}
          </button>
        }
      >
        <ul className="space-y-3 text-sm">
          <li className="flex items-center justify-between">
            <span>Frontend</span>
            <Badge tone="ok" dot>Online</Badge>
          </li>
          <li className="flex items-center justify-between">
            <span>Backend</span>
            <Badge tone={tone(health?.backend)} dot>{label(health?.backend)}</Badge>
          </li>
          <li className="flex items-center justify-between">
            <span>PostgreSQL</span>
            <Badge tone={tone(health?.database)} dot>{label(health?.database)}</Badge>
          </li>
        </ul>

        {health && (
          <p className="mt-4 font-mono text-xs text-ink-muted">
            fila: {health.details?.queue ?? "database"} · latência do banco:{" "}
            {health.details?.database.latency_ms ?? "—"} ms · {health.timestamp}
          </p>
        )}

        {error && (
          <p className="mt-4 rounded-md border border-danger/40 bg-danger/10 p-3 font-mono text-xs text-danger">
            {error} — suba os containers com <span className="font-bold">./scripts/start.sh</span>.
          </p>
        )}
      </Card>

      <Card title="Stack">
        <dl className="grid grid-cols-1 gap-3 font-mono text-xs text-ink-muted sm:grid-cols-3">
          <div>
            <dt className="text-ink">Backend</dt>
            <dd>{version?.stack.backend ?? "Laravel 12"}</dd>
          </div>
          <div>
            <dt className="text-ink">Frontend</dt>
            <dd>{version?.stack.frontend ?? "React + TypeScript + Vite"}</dd>
          </div>
          <div>
            <dt className="text-ink">Banco</dt>
            <dd>{version?.stack.database ?? "PostgreSQL 17"}</dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}
